import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Send,
  Trash2,
  Clock,
  CheckCircle,
  Home,
  X,
  MessageSquare,
  Bot,
  Users,
  FileText,
  RefreshCw,
  Download,
  Search,
  Bell,
  BellOff,
  AlertCircle,
  LogOut,
  RotateCcw,
  Settings,
  Loader2,
  FileDown,
  Phone,
  Power,
  PowerOff,
  ShieldAlert,
  Upload,
} from "lucide-react";
import ReviewsTab from "./ReviewsTab";
import DeliveriesTab from "./DeliveriesTab";
import MarketingTab from "./MarketingTab";
import ContactsTab from "./ContactsTab";
import ChatTab from "./ChatTab";
import SalesPipelineTab from "./sales/SalesPipelineTab";
import PhoneAgentTab from "./PhoneAgentTab";
import POSFileImport from "./POSFileImport";
import OrdersQuickStats from "./OrdersQuickStats";
import EmptyMessages from "../components/EmptyMessages";
import Sidebar, { useSidebarCollapsed } from "./Sidebar";
import { installPWA } from "./useReceiptDashboard";
import ReceiptCard from "./ReceiptCard";
import { supabase } from "../supabaseClient";
/**
 * ReceiptDashboardDesktop - Desktop UI Component
 * ENTERPRISE LIGHT MODE REDESIGN
 * Primary: Blue (#2563eb) | Secondary: Purple (#7c3aed)
 */

/**
 * ReceiptRowSkeleton — pulsing placeholder mirroring ReceiptCard's row
 * anatomy (status pill, customer block, stat boxes, total, toggle, buttons).
 * Shown during the initial receipts load so the list keeps its outline
 * instead of sitting blank; real rows populate in place.
 */
const ReceiptRowSkeleton = () => (
  <div
    className="relative bg-white rounded-xl border-[1.5px] border-slate-200 px-4 lg:px-5 py-3.5 flex items-center gap-3 lg:gap-4 animate-pulse"
    style={{ minHeight: "84px" }}
    aria-hidden="true"
  >
    <div className="h-6 w-28 rounded-full bg-slate-100 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="h-4 w-40 rounded bg-slate-200 mb-2" />
      <div className="h-3 w-28 rounded bg-slate-100" />
    </div>
    <div className="h-10 w-16 rounded-lg bg-slate-100 hidden md:block" />
    <div className="h-10 w-20 rounded-lg bg-slate-100 hidden md:block" />
    <div className="h-10 w-16 rounded-lg bg-slate-100 hidden lg:block" />
    <div className="h-5 w-16 rounded bg-slate-200 hidden sm:block" />
    <div className="h-8 w-24 rounded-full bg-slate-100 hidden xl:block" />
    <div className="h-9 w-24 rounded-md bg-slate-100" />
    <div className="h-9 w-9 rounded-md bg-slate-100" />
  </div>
);

const ReceiptDashboardDesktop = (props) => {
  const {
    loading,
    // Receipts
    receipts,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    receiptPeriodFilter,
    setReceiptPeriodFilter,
    receiptSelectedMonth,
    setReceiptSelectedMonth,
    sendingSMSReceiptId,
    smsSentReceipts,

    // PWA
    showInstallPrompt,
    setShowInstallPrompt,
    isInstalled,
    setIsInstalled,

    // Scanner & Invoices
    activeView,
    setActiveView,
    tabVisibility,
    visibleTabCount,
    // Store
    stores,
    currentStore,
    setCurrentStore,
    // SMS-AI emergency kill switch
    smsAiPaused,
    togglingSmsAi,
    toggleSmsAiPaused,
    // Inventory
    allInventoryUnits,

    // Notifications
    unreadMessages,
    setShowStoreSettings,
    setShowSalesTaxExportModal,
    escalations,

    // Push Notifications
    pushSupported,
    pushEnabled,
    pushLoading,
    togglePush,

    // Constants
    deliveryStatuses,

    // Computed values
    newOrdersReceipts,
    completedOrdersReceipts,
    activeReceipts,
    completedTabNotifications,
    stats,

    // Handler functions
    addNotification,
    loadReceipts,
    loadStoreSettings,
    loadInventoryUnits,
    normalizeStatus,
    getStatusIcon,
    getItemReadinessByQuantity,
    readinessByReceiptId,
    getPaymentStatus,
    handleOpenModal,
    handleSendSMS,
    handleDeleteReceipt,
    handleOpenReceipt,
    handleToggleChatMode = () => {},
    togglingChatModeId = null,
    openLogoutConfirm,
    phoneAgentSettings = {},
    // Voice save + visit/lead status + older-calls pagination — built in
    // useReceiptDashboard (phoneAgentTabProps) and forwarded via {...dashboard}.
    // `{}` default: an out-of-date parent can't crash the spread; the tab's
    // own unwired-prop guard still fails visibly instead.
    phoneAgentTabProps = {},
  } = props;

  // ===== Phone Agent Knowledge =====
  // Loads { hours, qa, customInstructions } from /api/phone-agents-2/knowledge
  // and provides a save callback for the Agent Knowledge drawer inside
  // PhoneAgentTab. Kept here (rather than in the useReceiptDashboard hook) so
  // it lives next to the existing inline phone-agent fetch patterns below.
  //
  // Auth: knowledge.py uses sms_security (session-based). We pull the
  // Supabase access_token at request time and send it as a Bearer header,
  // matching the pattern used elsewhere (e.g. /api/push/dismiss).
  const [phoneAgentKnowledge, setPhoneAgentKnowledge] = useState(null);

  useEffect(() => {
    if (!currentStore?.id) {
      setPhoneAgentKnowledge(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          console.warn("No Supabase session — skipping knowledge load");
          return;
        }
        const res = await fetch(
          `/api/phone-agents-2/knowledge?store_id=${currentStore.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled && data?.knowledge) {
          setPhoneAgentKnowledge(data.knowledge);
        }
      } catch (err) {
        console.error("Load phone agent knowledge failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStore?.id]);

  const handleSavePhoneAgentKnowledge = useCallback(
    async (next) => {
      if (!currentStore?.id) throw new Error("No store selected");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in — please refresh and try again");
      const res = await fetch("/api/phone-agents-2/knowledge", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ store_id: currentStore.id, ...next }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      if (data?.knowledge) setPhoneAgentKnowledge(data.knowledge);
    },
    [currentStore?.id]
  );

  // ===== Phone Agent Current Deals =====
  // Loads the store's marketing_designs tagged is_current_deal=true. These
  // surface in the new "Current Deals" tab inside the Agent Knowledge drawer
  // (PhoneAgentTab), and the agent prompt prioritizes them when callers ask
  // about sales/specials. Two creation paths land in the same list:
  //   1) Direct upload from the drawer (via /api/phone-agents-2/current-deals)
  //   2) Flyer Builder save with format_id='current-deal' (via /api/designs)
  // Either way, we GET them here in one query and pipe them down.
  const [phoneAgentDeals, setPhoneAgentDeals] = useState([]);

  const fetchPhoneAgentDeals = useCallback(async () => {
    if (!currentStore?.id) {
      setPhoneAgentDeals([]);
      return;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      const res = await fetch(
        `/api/phone-agents-2/current-deals?store_id=${currentStore.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPhoneAgentDeals(Array.isArray(data?.deals) ? data.deals : []);
    } catch (err) {
      console.error("Load current deals failed:", err);
    }
  }, [currentStore?.id]);

  useEffect(() => {
    fetchPhoneAgentDeals();
  }, [fetchPhoneAgentDeals]);

  // Image upload helper — uses the same 'flyers' bucket FlyerBuilder uses,
  // keyed under <storeId>/deals/ so they're visually grouped in storage.
  // Returns the public URL or throws.
  const handleUploadDealImage = useCallback(
    async (file) => {
      if (!currentStore?.id) throw new Error("No store selected");
      if (!file) throw new Error("No file");
      const ext = (file.name?.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
      const path = `${currentStore.id}/deals/${Date.now()}.${ext || "jpg"}`;
      const { error: uploadError } = await supabase.storage
        .from("flyers")
        .upload(path, file, {
          contentType: file.type || "image/jpeg",
          cacheControl: "3600",
          upsert: false,
        });
      if (uploadError) {
        // Fall back to a friendlier message when the bucket isn't set up yet
        if (String(uploadError.message || "").toLowerCase().includes("not found")) {
          throw new Error(
            "Storage bucket 'flyers' is missing. Create it in Supabase Storage with public access."
          );
        }
        throw uploadError;
      }
      const { data: urlData } = supabase.storage.from("flyers").getPublicUrl(path);
      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error("Could not resolve public URL after upload.");
      return publicUrl;
    },
    [currentStore?.id]
  );

  const handleCreateCurrentDeal = useCallback(
    async ({ thumbnail_url, description, title }) => {
      if (!currentStore?.id) throw new Error("No store selected");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in — please refresh and try again");
      const res = await fetch("/api/phone-agents-2/current-deals", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          store_id: currentStore.id,
          thumbnail_url,
          description,
          title,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      // Re-fetch to keep state authoritative; optimistic prepend is handled
      // inside PhoneAgentTab so the UI updates instantly either way.
      fetchPhoneAgentDeals();
      return data?.deal || null;
    },
    [currentStore?.id, fetchPhoneAgentDeals]
  );

  const handleDeleteCurrentDeal = useCallback(
    async (id, hard = false) => {
      if (!currentStore?.id) throw new Error("No store selected");
      if (!id) throw new Error("Missing deal id");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in — please refresh and try again");
      const qs = new URLSearchParams({
        store_id: currentStore.id,
        id,
      });
      if (hard) qs.set("hard", "1");
      const res = await fetch(`/api/phone-agents-2/current-deals?${qs.toString()}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Delete failed (HTTP ${res.status})`);
      }
      fetchPhoneAgentDeals();
    },
    [currentStore?.id, fetchPhoneAgentDeals]
  );

  // ===== Phone Agent Notification Recipients =====
  // Loads notification_recipients from /api/phone-agents-2/config (the array
  // of {id, name, phone} that gets texted on every call). Same auth pattern
  // as knowledge (Supabase Bearer token). The GET endpoint auto-promotes
  // legacy single-number stores into a one-row array so the UI shows
  // existing data even before the user touches anything.
  //
  // The same /api/phone-agents-2/config response also carries transfer_contacts
  // (the call-forwarding directory). We populate both states from the single
  // fetch below — no need for a parallel useEffect.
  const [phoneAgentRecipients, setPhoneAgentRecipients] = useState([]);
  const [phoneAgentTransferContacts, setPhoneAgentTransferContacts] = useState([]);
  // Kill-switch state. `is_active` is fetched in the same /config GET below.
  // Default true so the toggle reads "live" until the load proves otherwise —
  // a momentary "paused" flash on first paint would be misleading.
  const [phoneAgentActive, setPhoneAgentActive] = useState(true);

  useEffect(() => {
    if (!currentStore?.id) {
      setPhoneAgentRecipients([]);
      setPhoneAgentTransferContacts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          console.warn("No Supabase session — skipping recipients load");
          return;
        }
        const res = await fetch(
          `/api/phone-agents-2/config?store_id=${currentStore.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setPhoneAgentRecipients(
            Array.isArray(data?.notification_recipients)
              ? data.notification_recipients
              : []
          );
          setPhoneAgentTransferContacts(
            Array.isArray(data?.transfer_contacts)
              ? data.transfer_contacts
              : []
          );
          // Kill switch: the config GET returns is_active. Treat only an
          // explicit false as paused; anything missing/true reads as live.
          setPhoneAgentActive(data?.is_active !== false);
        }
      } catch (err) {
        console.error("Load phone agent recipients failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStore?.id]);

  const handleSavePhoneAgentRecipients = useCallback(
    async (cleaned) => {
      if (!currentStore?.id) throw new Error("No store selected");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in — please refresh and try again");
      const res = await fetch("/api/phone-agents-2/config", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          store_id: currentStore.id,
          notification_recipients: cleaned,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      if (Array.isArray(data?.notification_recipients)) {
        // Update local state with the server-normalized version (E.164 phones,
        // deduped, capped) so the UI reflects exactly what was persisted.
        setPhoneAgentRecipients(data.notification_recipients);
      }
    },
    [currentStore?.id]
  );

  // ===== Phone Agent Transfer Contacts (Call Forwarding) =====
  // Mirrors the recipients save handler exactly. POSTs to the same
  // /api/phone-agents-2/config endpoint — the backend will run the contacts
  // through coerce_transfer_contacts (name+phone required, E.164 normalized,
  // deduped, capped at 10) and return the server-normalized array back.
  const handleSavePhoneAgentTransferContacts = useCallback(
    async (cleaned) => {
      if (!currentStore?.id) throw new Error("No store selected");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in — please refresh and try again");
      const res = await fetch("/api/phone-agents-2/config", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          store_id: currentStore.id,
          transfer_contacts: cleaned,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      if (Array.isArray(data?.transfer_contacts)) {
        // Server-normalized list (E.164 phones, deduped, name-collisions
        // disambiguated). Reflect exactly what was persisted.
        setPhoneAgentTransferContacts(data.transfer_contacts);
      }
    },
    [currentStore?.id]
  );

  const [showEscalations, setShowEscalations] = useState(false);
  const [showFileImport, setShowFileImport] = useState(false);

  // ===== SMS-AI emergency kill switch (UI glue) =====
  // The hook owns the state + persistence (toggleSmsAiPaused). Here we add the
  // confirm-before-pausing gate (pausing kills AI replies for ALL customers,
  // so it's destructive enough to confirm; resuming is one click). Errors
  // surface via addNotification.
  const [smsKillConfirmOpen, setSmsKillConfirmOpen] = useState(false);

  const handleSmsAiSwitchClick = useCallback(() => {
    if (togglingSmsAi) return;
    if (smsAiPaused) {
      // Resume immediately — re-enabling should be frictionless.
      toggleSmsAiPaused(false).catch((e) =>
        addNotification?.(e?.message || "Couldn't resume AI", "error")
      );
    } else {
      setSmsKillConfirmOpen(true);
    }
  }, [smsAiPaused, togglingSmsAi, toggleSmsAiPaused, addNotification]);

  const confirmSmsAiPause = useCallback(() => {
    setSmsKillConfirmOpen(false);
    toggleSmsAiPaused(true).catch((e) =>
      addNotification?.(e?.message || "Couldn't pause AI", "error")
    );
  }, [toggleSmsAiPaused, addNotification]);

  // ===== Phone Agent kill switch =====
  // Flips phone_agent_settings.is_active via the same /config POST the other
  // phone-agent saves use (session Bearer auth). is_active is enforced
  // end-to-end: the voice server's tenant lookup (tenant.py) and every tool
  // endpoint filter is_active=eq.true, so false = the agent stops answering.
  // We optimistically set local state, then reconcile to the server's echoed
  // value; PhoneAgentTab does its own optimistic flip + rollback on throw, so
  // we just need to throw on failure.
  const handleTogglePhoneAgent = useCallback(
    async (nextEnabled) => {
      if (!currentStore?.id) throw new Error("No store selected");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in — please refresh and try again");
      const res = await fetch("/api/phone-agents-2/config", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          store_id: currentStore.id,
          is_active: nextEnabled,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      // Reflect the server's persisted value (falls back to what we sent).
      setPhoneAgentActive(
        typeof data?.is_active === "boolean" ? data.is_active : nextEnabled
      );
    },
    [currentStore?.id]
  );

  // PERF: Memoize so an unrelated state change (polling, unread count, etc.)
  // doesn't recreate this array and force ReceiptCard children to re-render.
  const filteredActiveReceipts = useMemo(() => (
    !showEscalations
      ? activeReceipts || []
      : (activeReceipts || []).filter(
          (receipt) => (escalations || {})[receipt.id] !== undefined,
        )
  ), [activeReceipts, escalations, showEscalations]);

  // PERF: Fast drop-in for getItemReadinessByQuantity.
  // Same signature, same return shape — but does an O(1) lookup against the
  // precomputed readinessByReceiptId map first. Falls back to the original
  // function for any receipt not yet in the map (just-added, edge cases),
  // which guarantees identical behavior to the previous implementation.
  // ReceiptCard's code is unchanged; it still calls
  // getItemReadinessByQuantity(receipt, allInventoryUnits) as before.
  const fastGetItemReadinessByQuantity = useCallback(
    (receipt, globalInventory) =>
      readinessByReceiptId?.[receipt?.id]
        || getItemReadinessByQuantity(receipt, globalInventory),
    [readinessByReceiptId, getItemReadinessByQuantity],
  );

  // Sidebar collapse state (persisted to localStorage via the hook)
  const {
    collapsed: sidebarCollapsed,
    toggle: toggleSidebar,
    width: sidebarWidth,
  } = useSidebarCollapsed();

  // Centralized navigation side-effects (replaces per-button onClick logic)
  const handleNavigate = (view) => {
    setActiveView(view);
    if (view === "orders" || view === "deliveries") {
      loadReceipts(currentStore?.id, true); // silent: refresh in place, no spinner
    }
  };

  const handleStoreChange = (store) => {
    setCurrentStore(store);
    localStorage.setItem("selectedStoreId", store?.id || "");
    loadReceipts(store?.id);
    loadInventoryUnits(store?.id);
    loadStoreSettings(store?.id);
  };

  const handleInstallPWA = async () => {
    const installed = await installPWA();
    if (installed) {
      setIsInstalled(true);
      setShowInstallPrompt(false);
    }
  };

  // Orders badge: count of receipts not yet delivered
  const ordersBadgeCount = (receipts || []).filter(
    (r) => (r.deliveryStatus || r.delivery_status) !== "delivered",
  ).length;

  // Enterprise Light Mode Status Colors - PREMIUM HIGH CONTRAST
  const getEnterpriseStatusColor = (status) => {
    switch (status) {
      case "new":
      case "pending":
        return "bg-blue-100 text-blue-800 border-blue-400";
      case "processing":
        return "bg-slate-100 text-slate-700 border-slate-400";
      case "ready":
        return "bg-emerald-100 text-emerald-800 border-emerald-400";
      case "out_for_delivery":
        return "bg-cyan-100 text-cyan-800 border-cyan-400";
      case "partial_out_for_delivery":
        return "bg-indigo-100 text-indigo-800 border-indigo-400";
      case "partially_delivered":
        return "bg-amber-100 text-amber-800 border-amber-400";
      case "delivered":
        return "bg-slate-200 text-slate-700 border-slate-500";
      default:
        return "bg-gray-100 text-gray-700 border-gray-400";
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar Navigation */}
      <Sidebar
        activeView={activeView}
        onNavigate={handleNavigate}
        tabVisibility={tabVisibility}
        visibleTabCount={visibleTabCount}
        stores={stores}
        currentStore={currentStore}
        onStoreChange={handleStoreChange}
        ordersBadgeCount={ordersBadgeCount}
        onOpenSettings={() => setShowStoreSettings(true)}
        onOpenSalesTaxExport={() => setShowSalesTaxExportModal(true)}
        pushSupported={pushSupported}
        pushEnabled={pushEnabled}
        pushLoading={pushLoading}
        onTogglePush={togglePush}
        showInstallPrompt={showInstallPrompt}
        isInstalled={isInstalled}
        onInstallPWA={handleInstallPWA}
        onLogout={openLogoutConfirm}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebar}
      />

      {/* Desktop Content Area */}
      <div
        className="hidden lg:block transition-[margin] duration-200 ease-out"
        style={{ marginLeft: sidebarWidth }}
      >
        <div className="px-6 pt-6 pb-6 max-w-[1920px] mx-auto">
        {/* Orders View */}
        {activeView === "orders" && tabVisibility.orders && (
          <div className="overflow-hidden max-h-[calc(100vh-3rem)] flex flex-col rounded-2xl border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)] bg-white">
            {/* Header Section */}
            <div className="flex-shrink-0 bg-gradient-to-b from-white to-slate-50/80 backdrop-blur-xl p-4 border-b border-slate-200 sticky top-0 z-10 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">
                    Order <span className="text-blue-600">Receipts</span>
                  </h2>
                  <p className="text-slate-500 text-xs mt-0.5">
                    Track and manage customer orders
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* SMS-AI emergency kill switch. Distinct from the per-receipt
                      "take over" toggle — this stops AI replies for EVERY
                      customer chat at once. When paused it's hidden here and the
                      banner below owns the single Resume button (no duplicate). */}
                  {!smsAiPaused && (
                    <button
                      onClick={handleSmsAiSwitchClick}
                      disabled={togglingSmsAi || !currentStore?.id}
                      title="Emergency: stop the AI from replying to all customer texts"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-rose-600 border border-rose-200 hover:bg-rose-50 hover:border-rose-300 font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {togglingSmsAi ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <PowerOff className="w-4 h-4" />
                      )}
                      <span>Pause AI</span>
                    </button>
                  )}
                  <button
                    onClick={() => setShowFileImport(true)}
                    title="Import a POS export file (STORIS, Furniture Wizard, any CSV/Excel)"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 font-semibold text-sm transition-all"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Import File</span>
                  </button>
                  <button
                    onClick={() => handleOpenModal()}
                    data-tip="Opens a blank receipt form for a new order"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-semibold text-sm transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    <span>New Receipt</span>
                  </button>
                </div>
              </div>

              {/* SMS-AI paused banner — loud, owns the single Resume button */}
              {smsAiPaused && (
                <div className="mb-3 px-3 py-2.5 bg-rose-50 border border-rose-200 rounded-lg flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
                    </span>
                    <ShieldAlert className="w-4 h-4 text-rose-600 flex-shrink-0" />
                    <span className="text-sm text-rose-900 font-medium min-w-0">
                      <span className="font-bold">AI replies paused.</span> Customer
                      texts are not being answered automatically — staff must reply.
                    </span>
                  </div>
                  <button
                    onClick={handleSmsAiSwitchClick}
                    disabled={togglingSmsAi}
                    data-tip="Lets the AI answer customer texts again right away"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 shadow-sm"
                  >
                    {togglingSmsAi ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Power className="w-4 h-4" />
                    )}
                    <span>Resume AI</span>
                  </button>
                </div>
              )}

              {/* Quick Stats Strip */}
              <OrdersQuickStats
                onStatClick={(key) => {
                  if (key === "escalations") setShowEscalations((v) => !v);
                }}
                receipts={receipts}
                unreadMessages={unreadMessages}
                escalations={escalations}
                getPaymentStatus={getPaymentStatus}
                stats={stats}
              />

              {/* Search & Filter Bar */}
              <div className="flex items-center gap-4">
                {/* Search Input */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="search"
                    name="receipt-search-desktop"
                    autoComplete="off"
                    data-form-type="other"
                    placeholder="Search by name, phone, or item..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 rounded-lg bg-white border-2 border-slate-300 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      aria-label="Clear search"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Period filter (order created date); OrdersQuickStats above uses full receipts */}
                {/* Inert while searching — search spans all time, so the period window is bypassed. */}
                <div className={`flex items-center gap-2 flex-shrink-0 flex-wrap transition-opacity ${searchQuery.trim() ? 'opacity-40 pointer-events-none' : ''}`} aria-disabled={!!searchQuery.trim()}>
                  <div
                    className="flex items-center gap-1 rounded-lg border-2 border-slate-300 bg-slate-100 p-1 shadow-sm"
                    role="group"
                    aria-label="Filter orders by time period"
                  >
                    {[
                      { id: "all", label: "All" },
                      { id: "week", label: "This week" },
                      { id: "month", label: "Month" },
                    ].map(({ id, label }) => (
                      <button
                        key={id}
                        type="button"
                        disabled={!!searchQuery.trim()}
                        onClick={() => setReceiptPeriodFilter(id)}
                        className={`px-3 py-2 rounded-md text-xs font-semibold transition-all whitespace-nowrap ${
                          receiptPeriodFilter === id
                            ? "bg-white text-blue-700 shadow-sm ring-2 ring-blue-200"
                            : "text-slate-600 hover:bg-white/80"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {receiptPeriodFilter === "month" && !searchQuery.trim() && (
                    <label className="flex items-center gap-2 text-xs font-medium text-slate-600 whitespace-nowrap">
                      <span className="sr-only">Month</span>
                      <input
                        type="month"
                        value={receiptSelectedMonth}
                        onChange={(e) => setReceiptSelectedMonth(e.target.value)}
                        className="rounded-lg border-2 border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </label>
                  )}
                </div>

                {/* Tab Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab("new")}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all border-2 ${
                      activeTab === "new"
                        ? "bg-blue-600 text-white border-blue-600 shadow-md"
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50 hover:border-slate-400 shadow-sm"
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    <span>Active</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        activeTab === "new"
                          ? "bg-white/20 text-white"
                          : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      {newOrdersReceipts.length}
                    </span>
                  </button>

                  <button
                    onClick={() => setActiveTab("completed")}
                    className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all border-2 ${
                      activeTab === "completed"
                        ? "bg-blue-600 text-white border-blue-600 shadow-md"
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50 hover:border-slate-400 shadow-sm"
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>Completed</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        activeTab === "completed"
                          ? "bg-white/20 text-white"
                          : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      {completedOrdersReceipts.length}
                    </span>
                    {/* Notification indicators */}
                    {(completedTabNotifications.escalationCount > 0 ||
                      completedTabNotifications.newMessageCount > 0) && (
                      <span className="absolute -top-1.5 -right-1.5">
                        {completedTabNotifications.escalationCount > 0 && (
                          <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full shadow">
                            {completedTabNotifications.escalationCount}
                          </span>
                        )}
                        {completedTabNotifications.newMessageCount > 0 &&
                          completedTabNotifications.escalationCount === 0 && (
                            <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-blue-500 rounded-full shadow">
                              {completedTabNotifications.newMessageCount}
                            </span>
                          )}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto bg-slate-100/70 shadow-[inset_0_2px_4px_-2px_rgba(15,23,42,0.06)]">
              {loading && (
                <div className="flex-1 flex items-center justify-center p-8">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-600">Loading orders...</p>
                  </div>
                </div>
              )}
              {/* Cross-tab search hint: current tab has no matches, but the other tab does. */}
              {!loading && searchQuery.trim() && filteredActiveReceipts.length === 0 &&
                ((activeTab === 'new' && completedOrdersReceipts.length > 0) ||
                 (activeTab === 'completed' && newOrdersReceipts.length > 0)) && (
                <div className="mx-4 mt-4 lg:mx-5 flex items-center justify-between gap-3 rounded-lg border-2 border-blue-200 bg-blue-50 px-4 py-3">
                  <span className="text-sm font-medium text-blue-800">
                    No matches in {activeTab === 'new' ? 'Active' : 'Completed'}, but{' '}
                    {activeTab === 'new' ? completedOrdersReceipts.length : newOrdersReceipts.length}{' '}
                    in {activeTab === 'new' ? 'Completed' : 'Active'}.
                  </span>
                  <button
                    type="button"
                    onClick={() => setActiveTab(activeTab === 'new' ? 'completed' : 'new')}
                    className="flex-shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
                  >
                    View {activeTab === 'new' ? 'Completed' : 'Active'}
                  </button>
                </div>
              )}
              {filteredActiveReceipts.length === 0 && !loading ? (
                <EmptyMessages
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  activeTab={activeTab}
                  receiptPeriodFilter={receiptPeriodFilter}
                  setReceiptPeriodFilter={setReceiptPeriodFilter}
                />
              ) : (
                <div className="p-4 lg:p-5 flex flex-col gap-2.5">
                  {/* Initial-load skeletons: keep the list's outline until
                      receipts arrive (mirrors the mobile pattern). Store
                      switches with stale cards showing keep existing
                      behavior — this only fires when the list is empty. */}
                  {loading && filteredActiveReceipts.length === 0 && (
                    <>
                      <ReceiptRowSkeleton />
                      <ReceiptRowSkeleton />
                      <ReceiptRowSkeleton />
                      <ReceiptRowSkeleton />
                      <ReceiptRowSkeleton />
                    </>
                  )}
                  {filteredActiveReceipts.map((receipt) => (
                    <ReceiptCard
                      key={receipt.id}
                      receipt={receipt}
                      sendingSMSReceiptId={sendingSMSReceiptId}
                      smsSentReceipts={smsSentReceipts}
                      togglingChatModeId={togglingChatModeId}
                      unreadMessages={unreadMessages}
                      escalations={escalations}
                      allInventoryUnits={allInventoryUnits}
                      normalizeStatus={normalizeStatus}
                      getItemReadinessByQuantity={fastGetItemReadinessByQuantity}
                      getPaymentStatus={getPaymentStatus}
                      handleOpenReceipt={handleOpenReceipt}
                      handleSendSMS={handleSendSMS}
                      handleDeleteReceipt={handleDeleteReceipt}
                      handleToggleChatMode={handleToggleChatMode}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Deliveries View — Testing Store only (deliveriesAccess.js).
            Strict === true: never mounts for a live store, even off a
            stale ?view=deliveries deep link. */}
        {activeView === "deliveries" && tabVisibility.deliveries === true && (
          <div className="overflow-hidden max-h-[calc(100vh-3rem)]">
            <DeliveriesTab
              storeId={currentStore?.id}
              store={currentStore}
              receipts={receipts}
              onRefresh={loadReceipts}
              onOpenSettings={() => setShowStoreSettings(true)}
              addNotification={addNotification}
            />
          </div>
        )}

        {/* Reviews View */}
        {activeView === "reviews" && tabVisibility.reviews !== false && (
          <div className="overflow-hidden max-h-[calc(100vh-3rem)]">
            <ReviewsTab
              storeId={currentStore?.id}
              store={currentStore}
              addNotification={addNotification}
            />
          </div>
        )}

        {/* Marketing Studio View — owner/testing login only (see
            MARKETING_OWNER_EMAILS in useReceiptDashboard.jsx). Strict
            === true: never mounts for live stores, even off a stale
            ?view=campaigns deep link. */}
        {activeView === "campaigns" && tabVisibility.campaigns === true && (
          <div className="overflow-hidden max-h-[calc(100vh-3rem)]">
            <MarketingTab
              storeId={currentStore?.id}
              store={currentStore}
              addNotification={addNotification}
            />
          </div>
        )}

        {/* Chat — one inbox per person. Testing Store only (chatAccess.js).
            Strict === true: never mounts for a live store. */}
        {activeView === "chat" && tabVisibility.chat === true && (
          <div className="h-[calc(100vh-3rem)] overflow-hidden">
            <ChatTab
              storeId={currentStore?.id}
              store={currentStore}
              addNotification={addNotification}
              onOpenReceipt={handleOpenReceipt}
            />
          </div>
        )}

        {/* Contacts — the store's text list: who opted in to deals by text
            (from the website's list page), who is still a lead, who opted
            out. Every store. */}
        {activeView === "contacts" && (
          <div className="overflow-hidden max-h-[calc(100vh-3rem)] h-full">
            <ContactsTab
              storeId={currentStore?.id}
              store={currentStore}
              addNotification={addNotification}
            />
          </div>
        )}

        {/* Sales — Exitium's pipeline for selling Exitium to furniture
            stores. Strict === true: only the Testing Store on the owner
            login (salesPipelineAccess.js); never mounts for a live store,
            even off a stale ?view=sales deep link. */}
        {activeView === "sales" && tabVisibility.sales === true && (
          <div className="h-[calc(100vh-3rem)] overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-50/60">
            <SalesPipelineTab
              storeId={currentStore?.id}
              store={currentStore}
              addNotification={addNotification}
            />
          </div>
        )}

        {/* Phone Agent View */}
        {activeView === "phoneAgent" && (tabVisibility.phoneAgent !== false) && (
          <PhoneAgentTab
            key={`pat-${currentStore?.id || "no-store"}-${phoneAgentKnowledge ? "k" : "pending"}-${phoneAgentRecipients.length}-${phoneAgentTransferContacts.length}-${phoneAgentDeals.length}`}
            currentStore={currentStore}
            addNotification={addNotification}
            agentNumber={phoneAgentSettings?.bland_phone_number || ""}
            agentEnabled={phoneAgentActive}
            onToggleAgent={handleTogglePhoneAgent}
            initialRecipients={phoneAgentRecipients}
            onSaveRecipients={handleSavePhoneAgentRecipients}
            initialTransferContacts={phoneAgentTransferContacts}
            onSaveTransferContacts={handleSavePhoneAgentTransferContacts}
            initialKnowledge={phoneAgentKnowledge || undefined}
            onSaveKnowledge={handleSavePhoneAgentKnowledge}
            initialCurrentDeals={phoneAgentDeals}
            onUploadCurrentDeal={handleUploadDealImage}
            onCreateCurrentDeal={handleCreateCurrentDeal}
            onDeleteCurrentDeal={handleDeleteCurrentDeal}
            onRefreshCurrentDeals={fetchPhoneAgentDeals}
            onFetchCalls={async () => {
              if (!currentStore?.id || !phoneAgentSettings?.tool_auth_token) return [];
              const res = await fetch(
                `/api/phone-agents-2/calls?store_id=${currentStore.id}&limit=50`,
                { headers: { "X-Phone-Agent-Key": phoneAgentSettings.tool_auth_token } }
              );
              if (!res.ok) throw new Error(`Failed to fetch calls: ${res.status}`);
              const data = await res.json();
              return data.calls || [];
            }}
            onFetchLiveCalls={async () => {
              if (!currentStore?.id || !phoneAgentSettings?.tool_auth_token) return [];
              const res = await fetch(
                `/api/phone-agents-2/calls?store_id=${currentStore.id}&live=1`,
                { headers: { "X-Phone-Agent-Key": phoneAgentSettings.tool_auth_token } }
              );
              if (!res.ok) return [];
              const data = await res.json();
              return data.live || [];
            }}
            onFetchCallDetail={async (callId) => {
              if (!currentStore?.id || !phoneAgentSettings?.tool_auth_token) return null;
              const res = await fetch(
                `/api/phone-agents-2/calls?store_id=${currentStore.id}&call_id=${encodeURIComponent(callId)}`,
                { headers: { "X-Phone-Agent-Key": phoneAgentSettings.tool_auth_token } }
              );
              if (!res.ok) throw new Error(`Failed to fetch call: ${res.status}`);
              const data = await res.json();
              return data.call || null;
            }}
            onMarkCallReviewed={async (callId) => {
              if (!currentStore?.id || !phoneAgentSettings?.tool_auth_token) {
                throw new Error("Not ready");
              }
              const res = await fetch("/api/phone-agents-2/calls", {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                  "X-Phone-Agent-Key": phoneAgentSettings.tool_auth_token,
                },
                body: JSON.stringify({
                  call_id: callId,
                  store_id: currentStore.id,
                  reviewed: true,
                }),
              });
              if (!res.ok) throw new Error(`Failed to mark reviewed: ${res.status}`);
              return res.json();
            }}
            onOpenReceipt={(receiptId) => {
              if (!receiptId) return;
              const receipt = (receipts || []).find((r) => r.id === receiptId);
              if (receipt) {
                handleOpenReceipt(receipt);
              } else {
                // Fallback: open by ID shell so the modal can hydrate via fetchReceiptById
                handleOpenReceipt({ id: receiptId });
              }
            }}
            {...phoneAgentTabProps}
          />
        )}

        {/* SMS-AI kill switch — confirm before pausing all AI replies */}
        {showFileImport && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto">
            <div className="mt-10 w-full max-w-3xl">
              <POSFileImport
                currentStore={currentStore}
                onClose={() => setShowFileImport(false)}
                onImported={() => loadReceipts(currentStore?.id, true)}
              />
            </div>
          </div>
        )}

        {smsKillConfirmOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
              onClick={() => !togglingSmsAi && setSmsKillConfirmOpen(false)}
            />
            <div
              role="alertdialog"
              aria-modal="true"
              aria-label="Pause AI replies"
              className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
            >
              <div className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0">
                    <ShieldAlert className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-bold text-slate-900">
                      Pause all AI replies?
                    </h2>
                    <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">
                      The AI will immediately stop replying to every customer
                      text across all conversations. Staff will need to respond
                      manually until you resume it. This does not change your
                      per-conversation take-over settings.
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  onClick={() => setSmsKillConfirmOpen(false)}
                  disabled={togglingSmsAi}
                  className="px-3 py-2 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-slate-200/70 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSmsAiPause}
                  disabled={togglingSmsAi}
                  data-tip="Stops AI replies for every customer text until you resume"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-semibold text-white bg-rose-600 hover:bg-rose-700 active:bg-rose-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {togglingSmsAi ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Pausing…
                    </>
                  ) : (
                    <>
                      <PowerOff className="w-4 h-4" />
                      Pause AI
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptDashboardDesktop;